import React from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
} from "recharts";

interface BranchGraphProps {
    graphData: {
        y_axis: string[];
        x_axis: (number | string)[];
        data: any[];
        avg_data: any[];
    };
}

// Custom icon component for dots
const IconDot = (props: any) => {
    const { cx, cy, payload, value, dataKey } = props;

    if (!cx || !cy) return null;

    // Determine which dataset this point belongs to
    const isMainData = dataKey === "mainValue";
    const pointData = isMainData
        ? payload.mainData
        : payload.avgData;

    if (!pointData) return null;

    // Use different styles for main vs avg data
    const size = 11;

    return (
        <image
            href={pointData.icon}
            x={cx - size / 2}
            y={cy - size / 2}
            width={size}
            height={size}
            style={{ pointerEvents: "none" }}
        />
    );
};

export const BranchGraph: React.FC<BranchGraphProps> = ({ graphData }) => {
    if (!graphData) return <div>No data provided</div>;

    const { y_axis, x_axis, data, avg_data } = graphData;

    // Create a mapping from string values to numeric positions

    const yValueMap: Record<string, number> = {};
    y_axis?.forEach((value, index) => {
        yValueMap[value] = index;
    });

    // Transform data into the format Recharts expects
    const chartData = x_axis?.map(year => {
        // Find corresponding data points
        const mainPoint = data.find(d => String(d.year) === String(year));
        const avgPoint = avg_data.find(d => String(d.year) === String(year));

        // Create return object with all needed data in a flat structure
        return {
            year: String(year),
            // Map string values to numeric for plotting
            mainValue: mainPoint ? yValueMap[mainPoint.value] : null,
            avgValue: avgPoint ? yValueMap[avgPoint.value] : null,
            // Store original data for tooltip and custom rendering
            mainData: mainPoint,
            avgData: avgPoint,
        };
    });

    // Formatter for tooltips to show original values
    const tooltipFormatter = (value: any, name: string, props: any) => {
        if (name === "mainValue") {
            return [props.payload.mainData?.value || "No data", "Main"];
        }
        return [props.payload.avgData?.value || "No data", "Average"];
    };

    // Formatter for y-axis to show original labels
    const yAxisFormatter = (value: number) => {
        return y_axis[value] || "";
    };

    return (
        <div style={{ width: '100%', height: '80%' }}>
            <ResponsiveContainer width="99%" height="100%">
                <LineChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                    <XAxis
                        dataKey="year"
                        tick={{ fontSize: "0.69vw", color: "rgba(0, 0, 0, 0.6)", fontWeight: 500 }}
                        axisLine={{ stroke: 'rgba(0, 0, 0, 0.1)' }}
                        tickLine={false}
                        padding={{ left: 20, right: 20 }}
                    />
                    <YAxis
                        type="number"
                        domain={[0, y_axis?.length - 1]}
                        ticks={Array.from({ length: y_axis?.length }, (_, i) => i)}
                        tickFormatter={yAxisFormatter}
                        tick={{ fontSize: "0.69vw", color: "rgba(0, 0, 0, 0.6)", fontWeight: 500 }}
                        axisLine={{ stroke: 'rgba(0, 0, 0, 0.1)' }}
                        tickLine={false}
                        padding={{ top: 20, bottom: 20 }}

                    />
                    {/* <Tooltip formatter={tooltipFormatter} /> */}

                    {/* Average data line with icons (dashed) */}
                    <Line
                        name="Average"
                        type="linear"
                        dataKey="avgValue"
                        stroke="#E0E0E0"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={<IconDot />}
                        connectNulls
                        isAnimationActive={false}
                    />
                    {/* Main data line with icons */}
                    <Line
                        name="Performance"
                        type="linear"
                        dataKey="mainValue"
                        stroke="#E0E0E0"
                        strokeWidth={2}
                        dot={<IconDot />}
                        connectNulls
                        isAnimationActive={false}
                    />

                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

